import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { lensPath, set } from 'ramda';

import GoalsForm from './Form';
import Title from '../shared/Title';
import { fillSpace, breakpoints } from '../../constants/styles';
import {
  getQuestions,
  stringToNumber,
  getGoalNames,
  iOSSafari,
} from '../../constants/utils';
import ProgressBar from '../shared/ProgressBar';

import { HEADER_HEIGHT, FlowHeader } from './styles';

const SCROLL_OFFSET = 350;

const StyledGoalsFlow = styled.div`
  display: flex;
  flex-direction row;
  ${fillSpace}
`;

const FlowContent = styled.div`
  display: flex;
  align-items: center;
  height: calc(100vh - 50px);
  margin-top: ${HEADER_HEIGHT}px;
  padding: 0 20px;
  background-color: white;

  ${breakpoints.tablet} {
    width: 70%;
    margin-left: 15%;
    margin-right: 15%;
  }

  ${breakpoints.tabletLandscape} {
    width: 60%;
    margin-left: 20%;
    margin-right: 20%;
  }
`;

class GoalsFlow extends Component {
  state = {
    isFinished: false,
    questionnaire: [],
    focusedGoal: 0,
    focusedQuestion: 0,
    questionsAnswered: [],
    totalQuestions: 0,
    inputList: [],
    financialGoals: [],
    userAge: null,
  };

  componentDidMount = () => {
    this.initializeForm(this.props);
    this.setQuestionAsFocused();
    document.addEventListener('keyup', this.keyPress, false);
  }

  componentWillUnmount() {
    document.removeEventListener('keyup', this.keyPress, false);
  }

  setGoalAsFocused = (goalIndex = 0) => {
    this.setState(state => ({
      ...set(lensPath(['questionnaire', goalIndex, 'isFocused']), true, state),
      focusedGoal: goalIndex,
    }));
  }

  setGoalAsAnswered = (goalIndex = 0) => {
    this.setState(state => ({
      ...set(lensPath(['questionnaire', goalIndex, 'isAnswered']), true, state),
    }));
  }

  setQuestionAsFocused = (goalIndex = 0, questionIndex = 0) => {
    this.setGoalAsFocused(goalIndex);
    this.setState(state => ({
      ...set(lensPath(['questionnaire', goalIndex, 'questions', questionIndex, 'isFocused']), true, state),
      focusedGoal: goalIndex,
      focusedQuestion: questionIndex,
    }));
  }

  setAllAsBlur = (cb) => {
    const { questionnaire } = this.state;
    this.setState({
      ...this.state,
      questionnaire: questionnaire.map(goal => ({
        ...goal,
        isFocused: false,
        questions: goal.questions.map(question => ({
          ...question,
          isFocused: false,
        })),
      })),
    }, cb);
  }

  setGoalAnswers = (values, goalName, goalIndex) => {
    const { userAge } = this.state;
    this.setState(state => ({
      ...set(lensPath(['financialGoals', goalIndex]),
        this.formatValues({ ...values, name: goalName, age: userAge }), state),
    }));
  }

  formatValues = (values) => {
    const { age, expectedAge, moneyRequired, initialSavings, ...rest } = values;
    const yearstoExpense = expectedAge - age;

    return {
      ...rest,
      age,
      current_savings: stringToNumber(initialSavings),
      money_required: stringToNumber(moneyRequired),
      years_to_expense: yearstoExpense || 0,
    };
  }

  initializeForm = ({ questionnaire, maxQuestionsOffset = 0 }, cb) => {
    // FIXME: Analizar mejor el tema de los maxQuestions.
    // const maxQuestions = getMaxQuestions(questionnaire) - maxQuestionsOffset;
    const maxQuestions = 0;
    this.setState({
      questionnaire,
      totalQuestions: getQuestions(questionnaire).length + maxQuestions,
      goalNames: getGoalNames(questionnaire),
      maxQuestions,
    }, () => {
      this.updateInputList(cb);
    });
  }

  updateInputList = (cb) => {
    const inputsFound = document.querySelectorAll('.question');
    const inputList = [];
    inputsFound.forEach((input) => {
      inputList.push(input.id);
    });
    this.setState({ inputList }, cb);
  }

  injectNewQuestions = (newQuestions = [], goalIndex, questionIndex, maxQuestions, cb) => {
    const { questionnaire } = this.state;
    const newQuestionnaire = [...questionnaire];
    newQuestionnaire[goalIndex].questions
      .splice(questionIndex + 1, 0, ...[...newQuestions]);
    this.initializeForm({ questionnaire: newQuestionnaire, maxQuestionsOffset: maxQuestions }, cb);
  }

  removeNewQuestions = (newQuestions = [], goalIndex, questionIndex, maxQuestions, cb) => {
    const { questionnaire } = this.state;
    const newQuestionnaire = [...questionnaire];
    newQuestionnaire[goalIndex].questions.splice(questionIndex + 1, newQuestions.length);
    this.initializeForm({ questionnaire: newQuestionnaire, maxQuestionsOffset: maxQuestions }, cb);
  }

  handleQuestionFocus = (ev, index, goalId) => {
    const { questionnaire } = this.state;
    const element = ev.target;
    const currentGoalIndex = questionnaire.findIndex(goal => goal.value === goalId);
    this.setAllAsBlur(() => {
      this.setQuestionAsFocused(currentGoalIndex, index);
      /** We need to do it with this method (getBoundingClientRect) so we can preserve the
        * right offset even with relative positioned elements.
        */
      const offsetTop = element.getBoundingClientRect().top + window.scrollY;
      if (!iOSSafari) {
        window.scrollTo({ top: offsetTop - SCROLL_OFFSET });
      }
    });
  }

  handleQuestionChange = (ev) => {
    const inputId = ev.target.id;
    const inputName = ev.target.name;
    const inputValue = ev.target.value === '' ? null : Number(ev.target.value);

    if (inputName === 'age') {
      this.setState({ userAge: inputValue });
    }
    this.setState({ currentInputId: inputId });
  }

  handleQuestionBlur = (ev, { values = [], goalAnswered, goalIndex, goalName }) => {
    const arrValues = Object.keys(values).map(key => values[key]);
    this.setState(state => ({
      ...set(lensPath(['questionsAnswered', goalIndex]), arrValues.length, state),
    }));
    this.setGoalAnswers(values, goalName, goalIndex);

    if (goalAnswered) {
      this.setGoalAsAnswered(goalIndex);
    }
  }

  handleFormAnswered = ({ values = [], goalAnswered, goalIndex, goalName }, cb) => {
    const arrValues = Object.keys(values).map(key => values[key]);
    this.setState(state => ({
      ...set(lensPath(['questionsAnswered', goalIndex]), arrValues.length, state),
    }));
    this.setGoalAnswers(values, goalName, goalIndex);

    if (goalAnswered) {
      this.setGoalAsAnswered(goalIndex);
    }
  }

  handleStepSubmit = () => {
    const { financialGoals } = this.state;
    const { onStepSubmit } = this.props;
    onStepSubmit(financialGoals);
  }

  goToNextInput = (questionId) => {
    const { inputList } = this.state;
    const currentInputIndex = inputList.findIndex(id => id === questionId);
    const nextInputName = inputList[currentInputIndex + 1];
    const nextInput = document.querySelector(`#${nextInputName}`);
    if (nextInput) {
      nextInput.focus();
      if (!iOSSafari) {
        window.scrollTo({ top: nextInput.offsetTop - SCROLL_OFFSET });
      }
    } else {
      this.setState({ isFinished: true });
      this.handleStepSubmit();
    }
  }

  keyPress = (ev) => {
    const { currentInputId } = this.state;
    if (ev.keyCode === 13 || ev.keyCode === 9) {
      ev.preventDefault();
      this.goToNextInput(currentInputId);
    }
  }

  render() {
    const {
      questionnaire,
      isFinished,
      userAge,
      questionsAnswered,
      totalQuestions,
    } = this.state;
    const totalAnswers = questionsAnswered.reduce((acc, cur) => (acc + cur), 0);
    return (
      <StyledGoalsFlow>
        <FlowHeader>
          <ProgressBar current={totalAnswers + 0.5} total={totalQuestions - 0.5} />
          <Title level={5} color="white" styles={css`margin-bottom: 0px;`} medium>Creando tu mapa financiero</Title>
        </FlowHeader>
        <FlowContent>
          <GoalsForm
            questionnaire={questionnaire}
            onQuestionFocus={this.handleQuestionFocus}
            onQuestionChange={this.handleQuestionChange}
            onQuestionBlur={this.handleQuestionBlur}
            onStepSubmit={this.handleStepSubmit}
            goToNextInput={this.goToNextInput}
            isFinished={isFinished}
            userAge={userAge}
            getSubmitRef={(ref) => { this.submitContainer = ref; }}
            injectNewQuestions={this.injectNewQuestions}
            removeNewQuestions={this.removeNewQuestions}
            handleFormAnswered={this.handleFormAnswered}
          />
        </FlowContent>
      </StyledGoalsFlow>
    );
  }
}

GoalsFlow.propTypes = {
  onStepSubmit: PropTypes.func,
};

GoalsFlow.defaultProps = {
  onStepSubmit: () => { },
};

export default GoalsFlow;
